import { useMutation } from "@tanstack/react-query";
import { api } from "./config";
import { ErrorResponsesCreateOrUpdate } from "../types/records.types";
import { OrderItem, OrderPayment } from "../screens/orders/ordersGridTypes.types";
import { queryClient } from "../main";
import { useContext } from "react";
import { RecordContext } from "../components/record/Record";

export const useCreateOrder = () => {
    const recordContext = useContext(RecordContext);

    return useMutation<any, ErrorResponsesCreateOrUpdate, any, any>({
        mutationFn: (formData: any) => createOrder(formData),
        onSuccess(data, variables) {
            createOrderItems(data.id, variables);
            createOrderPayments(data.id, variables);
            queryClient.invalidateQueries({
                predicate(query: any) {
                    return query.queryKey[0].startsWith("orders");
                },
            });
            recordContext?.entityForm.reset();
        },
    });
};

const createOrder = async (formData: any) => {
    const order = {
        clinic: formData.clinic?.id,
        treatment: formData.treatment?.id,
        payment_status: formData.payment_status,
        order_date: formData.order_date,
        notes: formData.notes,
        payment_date: formData.payment_status === "paid" ? formData.payment_date : null, 
    };

    const response = await api({
        method: "POST",
        url: `items/orders`,
        data: order,
    });

    return response.data.data;
};

export const createOrderItems = async (id: number, formData: any) => {
    if (!formData.order_items) return;

    const orderItemsData = formData.order_items as OrderItem[];

    const orderItems = orderItemsData.map((item: OrderItem) => {
        return {
            order_id: id,
            service_variant: item.variantId,
            quantity: item.quantity,
            price: item.price,
            discount: item.discount,
        };
    });

    const response = await api({
        method: "POST",
        url: `items/order_items`,
        data: orderItems,
    });

    return response.data.data;
};

export const createOrderItem = async (orderId: number, orderItem: OrderItem) => {
    const response = await api({
        method: "POST",
        url: `items/order_items`,
        data: {
            order_id: orderId,
            service_variant: orderItem.variantId,
            quantity: orderItem.quantity,
            price: orderItem.price,
            discount: orderItem.discount,
        },
    });

    return response.data.data;
};

export const getOrderPaymentsToSave = (formData: any) => {
    const orderPaymentsToSave: Array<OrderPayment> =
        formData.payment_status === "paid" ? formData.order_payments ?? [] : [];
    return orderPaymentsToSave;
};

export const createOrderPayments = async (orderId: number, formData: any) => {
    const orderPaymentsData = getOrderPaymentsToSave(formData);

    if (orderPaymentsData.length === 0) return;

    const orderPayments = orderPaymentsData.map((payment: OrderPayment) => {
        return {
            order: orderId,
            payment_method: payment.payment_method,
            amount: payment.amount,
            payment_date: payment.payment_date,
        };
    });

    const response = await api({
        method: "POST",
        url: `items/orders_payments`,
        data: orderPayments,
    });

    return response.data.data;
};
